'use client';

import { c as _c } from "react/compiler-runtime";
import { useUser } from '@clerk/nextjs';
import type { ReactNode } from 'react';
// this whole meta pixel topic had very bad documentation
// this is heavily AI coded
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { type ConsentStatus, getCookieConsent, handleGoogleAnalyticsConsent, handleMetaPixelConsent, setCookieConsent } from '../lib/consentUtils';
interface CookieConsentContextType {
  consentGiven: ConsentStatus;
  isMounted: boolean;
  hasConsent: boolean;
  isUndecided: boolean;
  acceptCookies: () => Promise<void>;
  declineCookies: () => Promise<void>;
}
const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};
interface CookieConsentProviderProps {
  children: ReactNode;
}
export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = t0 => {
  const $ = _c(17);
  const {
    children
  } = t0;
  const [consentGiven, setConsentGiven] = useState("undecided");
  const [isMounted, setIsMounted] = useState(false);
  const {
    user,
    isSignedIn
  } = useUser();
  const isSyncingRef = useRef(false);
  const lastSyncedConsentRef = useRef(null);
  let t1;
  if ($[0] !== isSignedIn || $[1] !== user) {
    t1 = [isSignedIn, user];
    $[0] = isSignedIn;
    $[1] = user;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(() => {
    const initializeConsent = async () => {
      const localConsent = getCookieConsent();
      if (isSignedIn && user) {
        const userConsent = user.publicMetadata?.cookieConsent as ConsentStatus;
        if (userConsent === "accepted") {
          setConsentGiven("accepted");
          setCookieConsent("accepted");
          lastSyncedConsentRef.current = "accepted";
        } else {
          if (userConsent === "rejected" && localConsent === "undecided") {
            setConsentGiven("undecided");
          } else {
            if (userConsent === "rejected" && localConsent === "rejected") {
              setConsentGiven("rejected");
              lastSyncedConsentRef.current = "rejected";
            } else {
              if (userConsent === "undecided" && localConsent === "undecided") {
                setConsentGiven("undecided");
              } else {
                if (localConsent !== "undecided" && localConsent !== userConsent) {
                  if (!isSyncingRef.current) {
                    isSyncingRef.current = true;
                    await updateUserConsent(localConsent);
                    lastSyncedConsentRef.current = localConsent;
                    isSyncingRef.current = false;
                  }
                  setConsentGiven(localConsent);
                } else {
                  setConsentGiven("undecided");
                }
              }
            }
          }
        }
      } else {
        setConsentGiven(localConsent);
      }
    };
    initializeConsent().then(() => {
      setIsMounted(true);
    });
  }, t1);
  const updateUser = _temp2;
  const updateUserConsent = async consent_0 => {
    if (isSignedIn && user) {
      ;
      try {
        if (lastSyncedConsentRef.current === consent_0) {
          return;
        }
        await updateUser(consent_0);
        lastSyncedConsentRef.current = consent_0;
      } catch (t2) {
        const error = t2;
        console.error("CookieConsentProvider: Failed to update user consent:", error);
      }
    }
  };
  let t3;
  if ($[3] !== updateUserConsent) {
    t3 = async () => {
      setCookieConsent("accepted");
      setConsentGiven("accepted");
      await updateUserConsent("accepted");
    };
    $[3] = updateUserConsent;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const acceptCookies = t3;
  let t4;
  if ($[5] !== updateUserConsent) {
    t4 = async () => {
      setCookieConsent("rejected");
      setConsentGiven("rejected");
      await updateUserConsent("rejected");
      handleGoogleAnalyticsConsent("rejected");
      handleMetaPixelConsent("rejected");
    };
    $[5] = updateUserConsent;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const declineCookies = t4;
  const hasConsent = consentGiven === "accepted";
  const isUndecided = consentGiven === "undecided";
  let t5;
  if ($[7] !== acceptCookies || $[8] !== consentGiven || $[9] !== declineCookies || $[10] !== hasConsent || $[11] !== isMounted || $[12] !== isUndecided) {
    t5 = {
      consentGiven,
      isMounted,
      hasConsent,
      isUndecided,
      acceptCookies,
      declineCookies
    };
    $[7] = acceptCookies;
    $[8] = consentGiven;
    $[9] = declineCookies;
    $[10] = hasConsent;
    $[11] = isMounted;
    $[12] = isUndecided;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const value = t5;
  let t6;
  if ($[14] !== children || $[15] !== value) {
    t6 = <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
    $[14] = children;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
function _temp() {
  return {};
}
async function _temp2(consent) {
  const updateMetadata = await fetch("/api/updateMetadata", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      cookieConsent: consent
    }),
    cache: "no-store"
  });
  const json = (await updateMetadata.json().catch(_temp)) as Record<string, unknown>;
  if (updateMetadata.status !== 200 || json?.message !== "success") {
    throw new Error("Error updating");
  }
}